// src/utils/connection.js
//
// Shared network/motion signals for deciding whether to serve a heavier,
// animated asset or a lighter, static fallback. Extracted from
// src/components/page/PageRenderer.jsx (its VideoBackground component),
// which already used this exact check to fall back from an autoplaying video
// to its poster image — the logo swap in Navbar.jsx is the same decision
// applied to a different asset, so it shares the same helper rather than
// re-deriving it.

// True when the visitor has Data Saver on, or the browser reports a 2G/3G
// connection. The Network Information API this reads (`navigator.connection`)
// has no Safari support at all — desktop or iOS — so it silently returns
// false there, same as any other unsupported browser. That's the deliberate,
// safe default: never withhold content because a signal is merely absent.
export const isDataConstrained = () => {
  if (typeof navigator === 'undefined') return false;
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) return false;
  if (c.saveData) return true;
  return /(^|-)2g$/.test(c.effectiveType || '') || c.effectiveType === '3g';
};

// True when the OS-level "reduce motion" accessibility setting is on. Unlike
// isDataConstrained, this is a real CSS media feature with full modern-browser
// support (including Safari) — it's the one signal here a user can reliably
// depend on actually working.
export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Subscribes to live connection changes (e.g. wifi -> cellular) and calls
// `onChange` when isDataConstrained()'s answer might have flipped. Returns an
// unsubscribe function, or a no-op if the browser exposes no `connection`
// object to listen on (again: Safari, most notably).
export const watchConnection = (onChange) => {
  if (typeof navigator === 'undefined') return () => {};
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c || !c.addEventListener) return () => {};
  c.addEventListener('change', onChange);
  return () => c.removeEventListener('change', onChange);
};
